import React, { useState } from 'react';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useAuthStore } from '@/stores/useAuthStore';
import {
  useGetApiWorkspacesWorkspaceIdMembers,
  usePutApiWorkspacesWorkspaceIdMembersUserIdRole,
  useDeleteApiWorkspacesWorkspaceIdMembersUserId,
  useDeleteApiWorkspacesWorkspaceIdInvitationsInvitationId,
} from '@/api/generated/workspaces/workspaces';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  PlusCircle,
  ShieldAlert,
  Crown,
  Shield,
  User,
  Trash2,
  Loader2,
} from 'lucide-react';
import { InviteMemberModal } from '../components/InviteMemberModal';
import { toast } from 'sonner';

export const TeamPage: React.FC = () => {
  const { activeWorkspaceId, currentRole, workspaces } = useWorkspaceStore();
  const { user } = useAuthStore();

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Member Management Dialog States
  const [memberToRemove, setMemberToRemove] = useState<any | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null);
  const [roleChangePending, setRoleChangePending] = useState<{ member: any; newRole: string } | null>(null);

  const {
    data: responseData,
    isLoading: isLoadingMembers,
    refetch: refetchTeamData,
  } = useGetApiWorkspacesWorkspaceIdMembers(
    activeWorkspaceId || '',
    { query: { enabled: !!activeWorkspaceId } }
  );

  const revokeMutation = useDeleteApiWorkspacesWorkspaceIdInvitationsInvitationId();
  const changeRoleMutation = usePutApiWorkspacesWorkspaceIdMembersUserIdRole();
  const removeMemberMutation = useDeleteApiWorkspacesWorkspaceIdMembersUserId();

  const activeWorkspaceName = workspaces.find((w) => w.id === activeWorkspaceId)?.name || 'Workspace';

  // Parse response data
  const dataObj = responseData as any;
  const membersList: any[] = Array.isArray(dataObj)
    ? dataObj
    : Array.isArray(dataObj?.members)
    ? dataObj.members
    : [];

  const invitationsList: any[] = Array.isArray(dataObj?.pendingInvitations)
    ? dataObj.pendingInvitations
    : Array.isArray(dataObj?.invitations)
    ? dataObj.invitations
    : [];

  const ownerCount = membersList.filter((m: any) => m.role === 'Owner').length;

  // Revoke pending invitation
  const handleRevokeInvitation = async (invitationId: string) => {
    if (!activeWorkspaceId) return;
    setRevokingId(invitationId);
    try {
      await revokeMutation.mutateAsync({
        workspaceId: activeWorkspaceId,
        invitationId,
      });
      toast.success('Convite revogado com sucesso.');
      refetchTeamData();
    } catch {
      // Global interceptor handles error toasts
    } finally {
      setRevokingId(null);
    }
  };

  // Role Change Logic
  const handleRoleSelect = (member: any, newRole: string) => {
    if (newRole === member.role) return;

    // If promoting to Owner, request confirmation first
    if (newRole === 'Owner') {
      setRoleChangePending({ member, newRole });
      return;
    }

    // If demoting an Owner, check if they are the last owner
    if (member.role === 'Owner' && ownerCount <= 1) {
      toast.error('Não é possível alterar a função do único Dono do workspace.');
      return;
    }

    executeRoleChange(member, newRole);
  };

  const executeRoleChange = async (member: any, newRole: string) => {
    if (!activeWorkspaceId) return;
    setUpdatingRoleId(member.userId);
    try {
      await changeRoleMutation.mutateAsync({
        workspaceId: activeWorkspaceId,
        userId: member.userId,
        data: { role: newRole as any },
      });
      toast.success(`Função de ${member.fullName || member.name || member.email} alterada para ${newRole}.`);
      refetchTeamData();
    } catch {
      // Global interceptor handles errors
    } finally {
      setUpdatingRoleId(null);
      setRoleChangePending(null);
    }
  };

  // Remove Member Logic
  const handleConfirmRemoveMember = async () => {
    if (!activeWorkspaceId || !memberToRemove) return;
    setIsRemoving(true);
    try {
      await removeMemberMutation.mutateAsync({
        workspaceId: activeWorkspaceId,
        userId: memberToRemove.userId,
      });
      toast.success(`Membro ${memberToRemove.name || memberToRemove.email} removido do workspace.`);
      refetchTeamData();
      setMemberToRemove(null);
    } catch {
      // Global interceptor handles errors
    } finally {
      setIsRemoving(false);
    }
  };

  // Helper to determine if the caller can remove a target member
  const canRemoveMember = (targetMember: any) => {
    if (!targetMember?.userId) return false;

    // An owner cannot remove themselves if they are the only owner
    if (targetMember.role === 'Owner' && ownerCount <= 1) {
      return false;
    }

    // Owner can remove anyone (including themselves if there's another owner)
    if (currentRole === 'Owner') {
      return true;
    }

    // Admin can ONLY remove Agents
    if (currentRole === 'Admin') {
      return targetMember.role === 'Agent';
    }

    return false;
  };

  if (currentRole === 'Agent') {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <ShieldAlert className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-bold">Acesso Negado</h2>
        <p className="text-muted-foreground">Você não tem permissão para visualizar esta página.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 lg:p-8 space-y-8 overflow-y-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Equipe</h1>
          <p className="text-muted-foreground">Gerencie os membros, funções e convites do seu workspace.</p>
        </div>
        <Button onClick={() => setIsInviteModalOpen(true)}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Convidar Membro
        </Button>
      </div>

      {/* Active Members Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight">Membros Ativos</h2>
          <span className="text-xs text-muted-foreground">
            {membersList.length} {membersList.length === 1 ? 'membro' : 'membros'}
          </span>
        </div>

        <div className="rounded-xl border bg-card text-card-foreground shadow-sm overflow-hidden">
          {isLoadingMembers ? (
            <div className="p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span>Carregando membros...</span>
            </div>
          ) : membersList.length > 0 ? (
            <div className="divide-y divide-border/60">
              {membersList.map((member: any, index: number) => {
                const memberId = member.userId || member.id || String(index);
                const name = member.fullName || member.name || member.userName || 'Sem nome';
                const email = member.email || member.userEmail || '';
                const role = member.role || 'Agent';
                const initial = (name || email || '?').charAt(0).toUpperCase();

                const isCurrentUser =
                  member.userId === user?.id ||
                  (email && user?.email && email.toLowerCase() === user.email.toLowerCase());

                const isOnlyOwner = role === 'Owner' && ownerCount <= 1;
                const isRoleUpdating = updatingRoleId === member.userId;
                const canRemove = canRemoveMember(member);

                return (
                  <div
                    key={memberId}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-4 hover:bg-muted/30 transition-colors"
                  >
                    {/* Member Profile Info */}
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="h-10 w-10 border border-border/50">
                        <AvatarFallback className="font-semibold text-xs bg-primary/10 text-primary">
                          {initial}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold truncate text-foreground">{name}</span>
                          {isCurrentUser && (
                            <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-4 font-normal">
                              Você
                            </Badge>
                          )}
                        </div>
                        {email && <span className="text-xs text-muted-foreground truncate">{email}</span>}
                      </div>
                    </div>

                    {/* Member Role & Actions */}
                    <div className="flex items-center justify-end gap-3 self-end sm:self-center">
                      {/* Role Selector or Badge */}
                      {currentRole === 'Owner' ? (
                        isOnlyOwner ? (
                          <Badge
                            variant="default"
                            className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 gap-1 font-medium py-1 px-2.5"
                          >
                            <Crown className="h-3 w-3 text-amber-500" />
                            <span>Dono</span>
                          </Badge>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            {isRoleUpdating && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                            <Select
                              value={role}
                              disabled={isRoleUpdating}
                              onValueChange={(newRole) => handleRoleSelect(member, newRole)}
                            >
                              <SelectTrigger className="w-[140px] h-8 text-xs font-medium">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Owner" className="text-xs">
                                  <div className="flex items-center gap-1.5">
                                    <Crown className="h-3.5 w-3.5 text-amber-500" />
                                    <span>Dono</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="Admin" className="text-xs">
                                  <div className="flex items-center gap-1.5">
                                    <Shield className="h-3.5 w-3.5 text-sky-500" />
                                    <span>Administrador</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="Agent" className="text-xs">
                                  <div className="flex items-center gap-1.5">
                                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span>Agente</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )
                      ) : (
                        /* Read-only role badge for Admins */
                        <Badge
                          variant="outline"
                          className={
                            role === 'Owner'
                              ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 gap-1'
                              : role === 'Admin'
                              ? 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/30 gap-1'
                              : 'text-muted-foreground gap-1'
                          }
                        >
                          {role === 'Owner' && <Crown className="h-3 w-3 text-amber-500" />}
                          {role === 'Admin' && <Shield className="h-3 w-3 text-sky-500" />}
                          {role === 'Agent' && <User className="h-3 w-3 text-muted-foreground" />}
                          <span>{role === 'Owner' ? 'Dono' : role === 'Admin' ? 'Administrador' : 'Agente'}</span>
                        </Badge>
                      )}

                      {/* Remove Member Button */}
                      {canRemove && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setMemberToRemove({ userId: member.userId, name, email, role })}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                          title={isCurrentUser ? 'Sair do workspace' : 'Remover do workspace'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-muted-foreground">Nenhum membro encontrado.</div>
          )}
        </div>
      </div>

      {/* Pending Invitations Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight">Convites Pendentes</h2>
          <span className="text-xs text-muted-foreground">
            {invitationsList.length} {invitationsList.length === 1 ? 'convite' : 'convites'}
          </span>
        </div>

        <div className="rounded-xl border bg-card text-card-foreground shadow-sm overflow-hidden">
          {isLoadingMembers ? (
            <div className="p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span>Carregando convites...</span>
            </div>
          ) : invitationsList.length > 0 ? (
            <div className="divide-y divide-border/60">
              {invitationsList.map((invite: any) => {
                const expiresAt = invite.expiresAtUtc || invite.expiresAt;
                const formattedExpiry = expiresAt
                  ? new Date(expiresAt).toLocaleDateString('pt-BR')
                  : 'N/A';

                return (
                  <div key={invite.id} className="flex items-center justify-between p-4 gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium truncate text-foreground">{invite.email}</span>
                        <span className="text-xs text-muted-foreground">
                          Expira em {formattedExpiry}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="text-xs">
                        {invite.role === 'Owner' ? 'Dono' : invite.role === 'Admin' ? 'Administrador' : 'Agente'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={revokingId === invite.id}
                        onClick={() => handleRevokeInvitation(invite.id)}
                        className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10 h-8"
                      >
                        {revokingId === invite.id ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                            <span>Revogando...</span>
                          </>
                        ) : (
                          'Revogar'
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-muted-foreground">Nenhum convite pendente.</div>
          )}
        </div>
      </div>

      {/* Invite Member Modal */}
      <InviteMemberModal 
        open={isInviteModalOpen} 
        onOpenChange={setIsInviteModalOpen}
        onSuccess={() => refetchTeamData()}
      />

      {/* Confirmation Dialog: Remove Member */}
      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover membro do workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover <strong>{memberToRemove?.name}</strong> ({memberToRemove?.email}) do workspace <strong>{activeWorkspaceName}</strong>? O usuário perderá acesso imediato a todas as conversas e configurações.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isRemoving}
              onClick={(e) => {
                e.preventDefault();
                handleConfirmRemoveMember();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-1.5"
            >
              {isRemoving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Removendo...</span>
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  <span>Remover Membro</span>
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation Dialog: Promote to Owner */}
      <AlertDialog open={!!roleChangePending} onOpenChange={(open) => !open && setRoleChangePending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" />
              <span>Promover para Dono do Workspace</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja promover <strong>{roleChangePending?.member?.name || roleChangePending?.member?.email}</strong> a <strong>Dono</strong>? Donos têm controle total sobre o workspace, incluindo gerenciar outros donos, alterar faturamento e excluir o workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updatingRoleId === roleChangePending?.member?.userId}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={updatingRoleId === roleChangePending?.member?.userId}
              onClick={(e) => {
                e.preventDefault();
                if (roleChangePending) {
                  executeRoleChange(roleChangePending.member, roleChangePending.newRole);
                }
              }}
              className="bg-amber-600 text-white hover:bg-amber-700 gap-1.5"
            >
              {updatingRoleId === roleChangePending?.member?.userId ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Promovendo...</span>
                </>
              ) : (
                <>
                  <Crown className="h-4 w-4" />
                  <span>Confirmar Promoção</span>
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamPage;
